import { FileWithPreview } from 'react-dropzone';
import { createCustomAction } from 'typesafe-actions';

import { ValidationErrorData } from '../../typings/common';
import { EditingFileInfo } from '../AddCreativePage/typings';
import { ApiGalleryItem } from '../common/requestsTypings';
import { ResourceType } from '../common/typings';
import { GalleryCollectionItem, GalleryPaneTypes } from '../GalleryPane/typings';

import { FileEditorOptions } from './typings';

export const uploadCancel = createCustomAction('@canvasFileEditor/UPLOAD_CANCEL', type => (editorId: string) => ({
    type,
    editorId
}));

export const uploadCancelSuccess = createCustomAction(
    '@canvasFileEditor/UPLOAD_CANCEL_SUCCESS',
    type => (editorId: string) => ({
        type,
        editorId
    })
);

export const uploadSourceSelected = createCustomAction(
    '@canvasFileEditor/UPLOAD_SOURCE_SELECTED',
    type => (editorId: string, resourceType: ResourceType, files: FileWithPreview[]) => ({
        type,
        editorId,
        resourceType,
        files
    })
);

export const linkSourceSelected = createCustomAction(
    '@canvasFileEditor/LINK_SOURCE_SELECTED',
    type => (editorId: string, urlType: ResourceType) => ({
        type,
        editorId,
        urlType
    })
);

export const reset = createCustomAction('@canvasFileEditor/RESET', type => (editorId: string) => ({
    type,
    editorId
}));

export const resetAll = createCustomAction('@canvasFileEditor/RESET_ALL', type => () => ({
    type
}));

export const init = createCustomAction(
    '@canvasFileEditor/INIT',
    type => (editorId: string, fileId: string | null, options: FileEditorOptions) => ({
        type,
        editorId,
        options,
        fileId
    })
);

export const showErrors = createCustomAction(
    '@canvasFileEditor/SHOW_ERRORS',
    type => (editorId: string, errors: ValidationErrorData) => ({
        type,
        editorId,
        errors
    })
);

export const setPreview = createCustomAction(
    '@canvasFileEditor/SET_PREVIEW',
    type => (editorId: string, item: ApiGalleryItem | GalleryCollectionItem, fileLoaded?: boolean) => ({
        type,
        editorId,
        item,
        fileLoaded
    })
);

export const setFile = createCustomAction(
    '@canvasFileEditor/SET_FILE',
    type => (editorId: string, item: ApiGalleryItem | GalleryCollectionItem) => ({
        type,
        editorId,
        item
    })
);

export const setEditorData = createCustomAction(
    '@canvasFileEditor/SET_EDITOR_DATA',
    type => (editingFileInfo: EditingFileInfo) => ({
        type,
        editingFileInfo
    })
);

export const sourceChange = createCustomAction('@canvasFileEditor/SOURCE_CHANGE', type => {
    return (resource: ResourceType, hasSourceItems: Partial<Record<GalleryPaneTypes, boolean>>) => ({
        type,
        resource,
        hasSourceItems
    });
});

export const waitForFileLoaded = createCustomAction(
    '@canvasFileEditor/WAIT_FOR_FILE_LOADED',
    type => (editorId: string, fileId: string | null) => ({
        type,
        editorId,
        fileId
    })
);

export const resetAudioFileId = createCustomAction('@canvasFileEditor/RESET_FILE_ID');

export const restoreAudioFileId = createCustomAction('@canvasFileEditor/RESTORE_AUDIO_FILE_ID');
export const crop = createCustomAction('@canvasFileEditor/CROP', type => (editorId: string) => ({
    type,
    editorId
}));

export const smartCenter = createCustomAction('@canvasFileEditor/SMART_CENTER', type => (editorId: string) => ({
    type,
    editorId
}));

export const hideLoading = createCustomAction('@canvasFileEditor/HIDE_LOADING', type => (editorId: string) => ({
    type,
    editorId
}));

export type UploadCancelAction = ReturnType<typeof uploadCancel>;
export type InitAction = ReturnType<typeof init>;
export type ResetAction = ReturnType<typeof reset>;
export type UploadSourceSelectedAction = ReturnType<typeof uploadSourceSelected>;
export type LinkSourceSelectedAction = ReturnType<typeof linkSourceSelected>;
export type WaitForFileLoadedAction = ReturnType<typeof waitForFileLoaded>;
export type CropAction = ReturnType<typeof crop>;
export type SmartCenterAction = ReturnType<typeof smartCenter>;

export type FileEditorAction =
    | UploadCancelAction
    | UploadSourceSelectedAction
    | LinkSourceSelectedAction
    | ResetAction
    | WaitForFileLoadedAction
    | CropAction
    | SmartCenterAction
    | ReturnType<typeof resetAll>
    | ReturnType<typeof showErrors>
    | ReturnType<typeof resetAudioFileId>
    | ReturnType<typeof restoreAudioFileId>
    | ReturnType<typeof sourceChange>
    | ReturnType<typeof uploadCancelSuccess>
    | ReturnType<typeof setPreview>
    | ReturnType<typeof setFile>
    | ReturnType<typeof hideLoading>
    | ReturnType<typeof setEditorData>
    | InitAction;
